import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Snackbar from '~/components/Snackbar';
import { useAuth } from '~/context/auth';
import { NO_PAG_LIST_OFFSET } from '~/data/constants';
import { ModuleInstance } from '~/data/models/yearbook';
import {
  DESTROY_MODULE_INSTANCE,
  MODULE_INSTANCES,
} from '~/data/operations/yearbook';
import {
  readModuleInstancesQuery,
  writeModuleInstancesQuery,
} from '~/data/operations/yearbook/helpers';
import { RootStackParamList, YearbookStackParamList } from '~/navigation/types';
import ManageLayout from './layout';

type ManageNavProp = CompositeNavigationProp<
  StackNavigationProp<YearbookStackParamList, 'Manage'>,
  StackNavigationProp<RootStackParamList>
>;

export default function Manage(): JSX.Element {
  const navigation = useNavigation<ManageNavProp>();

  const { authUserId, authGroupId } = useAuth();

  const openBannersInBrowser = (url: string) => {
    navigation.navigate('Browser', {
      title: 'Banner',
      url,
    });
  };

  const modulesInstancesVar = { group: authGroupId, first: NO_PAG_LIST_OFFSET };

  const { data: moduleInstancesData } = useQuery(MODULE_INSTANCES, {
    skip: !authGroupId,
    variables: modulesInstancesVar,
  });

  const moduleInstances: ModuleInstance[] =
    (moduleInstancesData?.moduleInstances?.edges.map(
      (edge) => edge?.node,
    ) as ModuleInstance[]) || [];

  const [destroyModuleInstance] = useMutation(DESTROY_MODULE_INSTANCE);

  const onDeleteModuleInstance = async (moduleInstanceId: string) => {
    try {
      await destroyModuleInstance({
        variables: {
          input: {
            id: moduleInstanceId,
          },
        },
        update(cache) {
          const currentModuleInstancesQuery = readModuleInstancesQuery({
            cache,
            variables: modulesInstancesVar,
          });
          if (
            currentModuleInstancesQuery &&
            currentModuleInstancesQuery.moduleInstances &&
            currentModuleInstancesQuery.moduleInstances.edges
          ) {
            const filteredEdges =
              currentModuleInstancesQuery.moduleInstances.edges.filter(
                (edge) => edge?.node?.id !== moduleInstanceId,
              );
            writeModuleInstancesQuery({
              cache,
              variables: modulesInstancesVar,
              data: {
                ...currentModuleInstancesQuery,
                moduleInstances: {
                  ...currentModuleInstancesQuery.moduleInstances,
                  edges: filteredEdges,
                },
              },
            });
          }
        },
      });
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  return (
    <ManageLayout
      onBack={() => navigation.goBack()}
      onExportData={() => navigation.navigate('ExportData')}
      onCollectedData={() => navigation.navigate('CollectedData')}
      onBookStats={() => navigation.navigate('BookStatistics')}
      openBannersInBrowser={openBannersInBrowser}
      authUserId={authUserId}
      moduleInstances={moduleInstances}
      onDeleteModuleInstance={onDeleteModuleInstance}
      onAddModule={() => navigation.navigate('AddModule')}
      onSelectModule={(moduleId) =>
        navigation.navigate('ModuleDetail', { moduleId, viewOnly: true })
      }
    />
  );
}
